import { useStore } from "@nanostores/preact"
import { useCallback, useEffect, useState } from "preact/hooks"

import { useTranslation } from "./Translation"
import { PriceDisplay } from "./PriceDisplay"
import { useUserData } from "../hooks/useUserData"
import { profile, promo } from "../services/store"
import { getCsrfToken } from "../services/backend"
import { applyPromoToAmount, formatAmountFromCents, promoIsApplied } from "../services/promo"
import { trackPromoCodeApplied, trackPromoCodeOpen } from "../services/analytics"

import css from "../style.module.scss"

export function PromoCodeSection({ token, className }: { token: string; className?: string }) {
  const t = useTranslation()
  const user = useStore(profile)
  const promoState = useStore(promo)
  const userData = useUserData()
  const [isValidatingPromo, setIsValidatingPromo] = useState(false)

  const clearPromoCode = () => {
    promo.set({
      autoApply: false,
      code: "",
      showField: false,
      validation: null,
    })
  }

  const validatePromoCode = useCallback(async () => {
    if (!promoState.code.trim()) {
      promo.setKey("validation", { error: "Please enter a promo code" })
      return
    }

    setIsValidatingPromo(true)
    promo.setKey("validation", null)

    try {
      const response = await fetch("/sign_up/validate_promo_code", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": getCsrfToken(token),
        },
        credentials: "include",
        body: JSON.stringify({ code: promoState.code }),
      })

      const data = await response.json()

      if (response.ok && data.valid) {
        promo.setKey("validation", data)
        trackPromoCodeApplied({
          promotion_code: promoState.code,
          promotion_code_id: data.promotion_code_id,
        })
      } else {
        promo.setKey("validation", {
          error: data.error || "Invalid promo code",
        })
      }
    } catch (error) {
      promo.setKey("validation", {
        error: "Failed to validate promo code",
      })
    } finally {
      setIsValidatingPromo(false)
    }
  }, [promoState.code, token])

  useEffect(() => {
    if (
      !promoState.autoApply ||
      !promoState.showField ||
      !promoState.code.trim() ||
      promoState.validation ||
      isValidatingPromo
    ) {
      return
    }

    promo.setKey("autoApply", false)
    validatePromoCode()
  }, [
    isValidatingPromo,
    promoState.autoApply,
    promoState.code,
    promoState.showField,
    promoState.validation,
    validatePromoCode,
  ])

  if (!userData) return null

  const { currency, products } = userData
  const currentPrice =
    user.subscription !== "free" ? products[user.subscription].prices[currency.acronym] : null

  const discount = promoIsApplied(promoState.validation) ? promoState.validation.discount : null
  const appliedPromo = Boolean(discount)
  const contentClassName = [css["promo-section"], className].filter(Boolean).join(" ")

  return (
    <div className={contentClassName}>
      {!promoState.showField && !appliedPromo && (
        <button
          type="button"
          onClick={() => {
            trackPromoCodeOpen()
            promo.setKey("showField", true)
          }}
          className={css["promo-code-link"]}
        >
          {t("have_promo_code") || "Have a promo code or gift card?"}
        </button>
      )}
      {(promoState.showField || appliedPromo) && (
        <>
          {promoState.showField && !appliedPromo && (
            <div className={css["promo-code-wrapper"]}>
              <input
                type="text"
                placeholder={t("promo_code_placeholder") || "Enter promo code"}
                value={promoState.code}
                onChange={(e) => {
                  promo.setKey("code", (e.target as HTMLInputElement).value)
                  promo.setKey("autoApply", false)
                  promo.setKey("validation", null)
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault()
                    if (promoState.code.trim() && !isValidatingPromo) {
                      validatePromoCode()
                    }
                  }
                }}
                className={css["promo-code-input"]}
                disabled={isValidatingPromo}
              />
              <button
                type="button"
                onClick={validatePromoCode}
                disabled={isValidatingPromo || !promoState.code.trim()}
                className={css["promo-code-button"]}
              >
                {isValidatingPromo ? t("validating") || "Validating..." : t("apply") || "Apply"}
              </button>
            </div>
          )}
          {discount && (
            <div className={css["promo-success"]}>
              <button
                type="button"
                onClick={clearPromoCode}
                className={css["promo-clear-button"]}
                aria-label="Remove promo code"
              >
                ×
              </button>
              {discount.percent_off
                ? `${discount.percent_off}% ${t("discount_applied") || "discount applied!"}`
                : discount.name ||
                  `${currency.prefix}${formatAmountFromCents(discount.amount_off || 0)} ${
                    t("discount_applied") || "discount applied!"
                  }`}
              {discount.duration === "repeating" &&
                discount.duration_in_months &&
                ` ${
                  t("promo_repeating_months", {
                    months: discount.duration_in_months,
                  }) || `(for ${discount.duration_in_months} months)`
                }`}
              {discount.duration === "once" &&
                ` ${t("promo_first_payment_only") || "(first payment only)"}`}
              {discount.duration === "forever" && ` ${t("promo_forever") || "(forever)"}`}
              {currentPrice && (
                <div className={css["discounted-price"]}>
                  {t("new_price") || "New price"}:{" "}
                  <PriceDisplay
                    originalAmount={currentPrice.unit_amount}
                    discountedAmount={applyPromoToAmount(
                      currentPrice.unit_amount,
                      currentPrice.currency,
                      promoState.validation,
                    )}
                    currency={currency}
                    originalClassName={css["promo-original-price"]}
                    discountedClassName={css["promo-current-price"]}
                  />
                </div>
              )}
            </div>
          )}
          {promoState.validation?.error && (
            <div className={css["promo-error"]}>{promoState.validation.error}</div>
          )}
        </>
      )}
    </div>
  )
}
